import type { LocationQuery } from 'vue-router'
import { addDays, differenceInCalendarDays, endOfDay } from 'date-fns'
import {
  DashboardType,
  type CalendarSummaryRequest,
  type ListFiltersRequest,
  type MatrixSummaryRequest
} from '@/Network/Types/Requests/StationRequests'
import type {
  CalendarSummaryResponse,
  ListStationsResponse
} from '@/Network/Types/Responses/StationResponses'
import type {
  DashboardSavedViewFilters,
  LensPeriod
} from '@/Network/Types/Requests/DashboardSavedViewRequests'
import { stripLensKeys, type LensState } from './lensState'
import { anchorFromRangeStart, getPeriodRange, resolveNavPeriod } from './monthCalendar'
import {
  matrixRange,
  parseMatrixQuery,
  stripMatrixKeys,
  toApiMetric,
  type MatrixCut,
  type MatrixMetricUi
} from '../matrix/matrixState'
import { stripUnitMapKeys } from '../unitMap/unitMapQuery'
import { parseCoverageStatuses, serializeCoverageStatuses } from '@/utils/coverageFilters'
import { parseDayKey, parseTimelineQuery, stripTimelineKeys } from '../timeline/timelineState'
import type { DemandTimelineRequest } from '@/Network/Types/Requests/DemandRequests'

/** Lote de escalas do Mês (spec §4.4, Q26). */
export const MONTH_SCHEDULES_BATCH = 12

const ARRAY_FIELDS = [
  'healthUnits',
  'schedules',
  'stationType',
  'operationalGroups',
  'sectorIds',
  'specialtyIds',
  'companySpecialtyIds'
] as const

// Mesmo tratamento que `Dashboard/Index.vue` já fazia no mount (a URL guarda 'true'/'false').
const BOOLEAN_FIELDS = [
  'withCheckin',
  'withCheckout',
  'inCash',
  'advanceRequested',
  'advancePaid',
  'ourTeam',
  'openedStation'
] as const

const TRANSIENT_FIELDS = ['type', 'schedulesLimit', 'schedulesOffset', 'limit', 'offset'] as const

/** Chaves de navegação da URL (deep link do detalhe do plantão): nunca são filtro da API. */
const NAVIGATION_FIELDS = ['stationId', 'exceptionId', 'openSidebar'] as const

/** Ids de catálogo vindos da URL: só inteiros positivos, em forma canônica ('081' → '81'). */
const ID_ARRAY_FIELDS = ['sectorIds', 'specialtyIds', 'companySpecialtyIds'] as const

function canonicalIds(values: unknown[]): string[] {
  const ids = values
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => /^\d+$/.test(value))
    .map((value) => String(Number(value)))
    .filter((value) => value !== '0')
  return [...new Set(ids)]
}

const VIEW_FILTER_KEYS: (keyof DashboardSavedViewFilters)[] = [
  'healthUnits',
  'schedules',
  'sectorIds',
  'specialtyIds',
  'companySpecialtyIds',
  'withoutSector',
  'withoutSpecialty',
  'openedStation',
  'coverageStatus'
]

const VIEW_ARRAY_KEYS: (keyof DashboardSavedViewFilters)[] = [
  'healthUnits',
  'schedules',
  'sectorIds',
  'specialtyIds',
  'companySpecialtyIds'
]

/**
 * Chips M10-A: `coverageStatus` viaja como CSV canônico (ordem do contrato, sem inválidos;
 * nada ou os 3 = sem filtro). Com ele presente, `openedStation` sai (D-B1: no BE
 * `openedStation=false` já é DOCTOR ∪ UNIDENTIFIED — os dois juntos fariam AND).
 */
function normalizeCoverageStatus(target: Record<string, unknown>) {
  if (!('coverageStatus' in target)) return
  const csv = serializeCoverageStatuses(parseCoverageStatuses(target.coverageStatus))
  if (csv) {
    target.coverageStatus = csv
    target.openedStation = undefined
  } else delete target.coverageStatus
}

/**
 * Na Matriz o tipo segue o recorte (`semana` → WEEK; `mes` → MONTHLY). A Timeline é um
 * dia só: intervalo livre (GENERAL), como o painel do dia do Calendário.
 */
export function periodToDashboardType(period: LensPeriod, cut?: MatrixCut): DashboardType {
  if (period === 'timeline') return DashboardType.GENERAL
  return resolveNavPeriod(period, cut) === 'semana' ? DashboardType.WEEK : DashboardType.MONTHLY
}

export function buildListQueryFromRoute(
  query: LocationQuery,
  lens: LensState,
  base: ListFiltersRequest,
  now: Date
): ListFiltersRequest {
  // `recorte`/`metrica` (Matriz) e `unidade`/`dia` (Timeline) nunca viram filtro da API.
  const { cut } = parseMatrixQuery(query)
  const { day } = parseTimelineQuery(query)
  // `unidade`/`momento`/`especialidades`/`tv` são estado do Mapa (Onda 2.4): idem. `unidade`
  // é compartilhada com a Timeline — só sai da query da API, nunca da URL.
  const fromUrl: Record<string, unknown> = stripUnitMapKeys(
    stripTimelineKeys(stripMatrixKeys(stripLensKeys({ ...query } as Record<string, unknown>)))
  )
  TRANSIENT_FIELDS.forEach((field) => delete fromUrl[field])
  NAVIGATION_FIELDS.forEach((field) => delete fromUrl[field])
  ARRAY_FIELDS.forEach((field) => {
    const value = fromUrl[field]
    if (value !== undefined && value !== null && !Array.isArray(value)) fromUrl[field] = [value]
  })
  ID_ARRAY_FIELDS.forEach((field) => {
    const value = fromUrl[field]
    if (!Array.isArray(value)) return
    const ids = canonicalIds(value)
    // Lista vazia (só lixo na URL) = sem filtro, como no resto da Dashboard.
    fromUrl[field] = ids.length ? ids : undefined
  })
  BOOLEAN_FIELDS.forEach((field) => {
    if (fromUrl[field] === 'true') fromUrl[field] = true
    if (fromUrl[field] === 'false') fromUrl[field] = false
  })
  normalizeCoverageStatus(fromUrl)
  if (!fromUrl.startHour) fromUrl.startHour = undefined
  if (!fromUrl.endHour) fromUrl.endHour = undefined

  // Âncora: data da URL → data da query base → hoje.
  const parseStart = (value: unknown): Date | null => {
    if (typeof value !== 'string' || !value) return null
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsedStart = parseStart(fromUrl.startDate) ?? parseStart(base.startDate)
  // Timeline: `dia` da URL é a âncora (link compartilhado abre o mesmo dia).
  const timelineDay = lens.period === 'timeline' ? parseDayKey(day) : null
  const anchor =
    timelineDay ?? (parsedStart ? anchorFromRangeStart(lens.period, parsedStart, cut) : now)
  const { startDate, endDate } = getPeriodRange(lens.period, anchor, cut)

  const next = {
    ...base,
    ...(fromUrl as Partial<ListFiltersRequest>),
    type: periodToDashboardType(lens.period, cut),
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  } as ListFiltersRequest
  delete next.schedulesOffset
  if (lens.period === 'mes') next.schedulesLimit = String(MONTH_SCHEDULES_BATCH)
  else delete next.schedulesLimit
  return next
}

export function toCalendarSummaryRequest(query: ListFiltersRequest): CalendarSummaryRequest {
  const copy: Record<string, unknown> = { ...query }
  TRANSIENT_FIELDS.forEach((field) => delete copy[field])
  return copy as CalendarSummaryRequest
}

/**
 * Query da Matriz: os mesmos filtros do calendário, com as datas do recorte (`matrixRange`,
 * sem os dias de fora do mês da grade) e a métrica no formato do BE. Sem `openedStation` nem `coverageStatus`:
 * a cobertura só faz sentido sobre TODOS os plantões (com "só vagas" tudo daria 0%, ex.:
 * depois do "Ver vagas" do painel e voltar à Matriz).
 */
export function toMatrixSummaryRequest(
  query: ListFiltersRequest,
  cut: MatrixCut,
  anchor: Date,
  metric: MatrixMetricUi
): MatrixSummaryRequest {
  const { startDate, endDate } = matrixRange(cut, anchor)
  const filters: Record<string, unknown> = { ...toCalendarSummaryRequest(query) }
  delete filters.openedStation
  // Idem para os chips de cobertura (M10-A), que substituem o `openedStation` na URL.
  delete filters.coverageStatus
  return {
    ...(filters as CalendarSummaryRequest),
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    metric: toApiMetric(metric)
  }
}

const toIds = (values: string[] | undefined): number[] | undefined => {
  const ids = (values ?? []).map(Number).filter((id) => Number.isInteger(id) && id > 0)
  return ids.length ? ids : undefined
}

const isTrue = (value: unknown): boolean => value === true || value === 'true'

/**
 * Query da Timeline (spec §5.2): só escalas, setor e especialidade do dashboard; a unidade
 * vem do seletor próprio. Médico, tipo, valores, check etc. não existem no DTO — um filtro
 * por tipo/médico criaria buracos falsos. Chaves vazias ficam de fora (chave de dedupe estável).
 */
export function toTimelineRequest(
  query: ListFiltersRequest,
  healthUnitId: number,
  date: string
): DemandTimelineRequest {
  const req: DemandTimelineRequest = { healthUnitId, date }
  const schedules = toIds(query.schedules)
  const sectorIds = toIds(query.sectorIds)
  const specialtyIds = toIds(query.specialtyIds)
  const companySpecialtyIds = toIds(query.companySpecialtyIds)
  if (schedules) req.schedules = schedules
  if (sectorIds) req.sectorIds = sectorIds
  if (specialtyIds) req.specialtyIds = specialtyIds
  if (companySpecialtyIds) req.companySpecialtyIds = companySpecialtyIds
  if (isTrue(query.withoutSector)) req.withoutSector = true
  if (isTrue(query.withoutSpecialty)) req.withoutSpecialty = true
  return req
}

/** Filtros do dashboard que a Timeline ignora (aviso fixo quando algum está ativo). */
const TIMELINE_IGNORED_FIELDS: (keyof ListFiltersRequest)[] = [
  'doctorIds',
  'doctorPending',
  'stationType',
  'valueMin',
  'valueMax',
  'paymentValueMin',
  'paymentValueMax',
  'withCheckin',
  'withCheckout',
  'inCash',
  'advanceRequested',
  'advancePaid',
  'ourTeam',
  'status',
  'openedStation',
  // Chips de cobertura (M10-A): substituem o `openedStation` na URL; a Timeline não os aplica.
  'coverageStatus',
  'operationalGroups',
  'startHour',
  'endHour'
]

export function timelineIgnoresFilters(query: ListFiltersRequest): boolean {
  return TIMELINE_IGNORED_FIELDS.some((field) => {
    const value = query[field] as unknown
    if (Array.isArray(value)) return value.length > 0
    return value !== undefined && value !== null && value !== ''
  })
}

export function extractViewFilters(query: ListFiltersRequest): DashboardSavedViewFilters {
  const filters: DashboardSavedViewFilters = {}
  VIEW_FILTER_KEYS.forEach((key) => {
    const value = query[key]
    if (Array.isArray(value)) {
      if (value.length) (filters as Record<string, string[]>)[key] = [...value]
    } else if (value !== undefined && value !== null && value !== '') {
      ;(filters as Record<string, string>)[key] = String(value)
    }
  })
  return filters
}

export function applyViewFilters(
  query: ListFiltersRequest,
  filters: DashboardSavedViewFilters
): ListFiltersRequest {
  const next = { ...query } as Record<string, unknown>
  VIEW_FILTER_KEYS.forEach((key) => {
    const value = filters[key]
    // Chaves de array nunca ficam `undefined`: consumidores (ex.: modais de exportação) leem `.length`/`.map`.
    next[key] = VIEW_ARRAY_KEYS.includes(key) ? (Array.isArray(value) ? [...value] : []) : value
  })
  normalizeCoverageStatus(next)
  return next as ListFiltersRequest
}

/** `MONTHLY_MAX_DAYS` do BE (`stations-errors.ts`): teto de dias de `GET /stations` MONTHLY e do calendar-summary. */
export const BACKEND_MAX_RANGE_DAYS = 42

/**
 * F3 (nota do BE, PR #2905). O BE filtra `station.date::date >= startDate::date` e
 * `station.date::date < endDate::date` (`station.typeorm.repository.ts`,
 * `applyStationScheduleFilters`/`findLimitedScheduleIdsByHealthUnits`), com `station.date`
 * gravado em UTC e o `::date` do parâmetro sendo a parte de data do ISO. Com o fim no
 * 23:59:59.999 local do último dia (ISO = dia seguinte 02:59Z), os plantões das 21h–23h59
 * locais desse dia (já no dia seguinte em UTC) ficam de fora. "Dia seguinte 00:00 local"
 * dá o mesmo `::date` e não resolve.
 *
 * Por isso o caminho V2 pede o período com o fim no 23:59:59.999 local do dia SEGUINTE
 * (o `::date` passa a ser 2 dias depois) e corta o dia extra da resposta
 * (`trimListToDays`/`trimCalendarSummary`). O BE conta as colunas com
 * `differenceInDays(end, start) + 1`: a resposta traz exatamente 1 coluna a mais.
 * Sem estender quando o período já tem o teto do BE (42 dias): `days = null`.
 */
export function withInclusiveEndDate<T extends { startDate: string; endDate: string }>(
  query: T
): { query: T; days: number | null } {
  const start = new Date(query.startDate)
  const end = new Date(query.endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return { query, days: null }
  const days = differenceInCalendarDays(end, start) + 1
  if (days < 1 || days + 1 > BACKEND_MAX_RANGE_DAYS) return { query, days: null }
  return { query: { ...query, endDate: endOfDay(addDays(end, 1)).toISOString() }, days }
}

type ListCompany = ListStationsResponse['data'][number]
type ListUnit = ListCompany['healthUnities'][number]
type ListSchedule = ListUnit['schedules'][number]
type ListShift = ListSchedule['shifts'][number]
type ListHour = ListShift['hours'][number]
type ListPosition = ListHour['positions'][number]

const cardCount = (columns: ListPosition['stations']) =>
  columns.reduce((sum, cards) => sum + (cards?.length ?? 0), 0)

/**
 * Poda só o que ficou vazio POR CAUSA do corte: um nó que já vinha sem filhos continua
 * (linha vazia legítima do período, Q24); um que tinha filhos e perdeu todos sai.
 */
function pruneEmptied<T>(before: T[], after: (T | null)[]): T[] | null {
  const kept = after.filter((item): item is T => item !== null)
  return before.length > 0 && kept.length === 0 ? null : kept
}

/**
 * Resposta de `GET /stations` só com as `days` primeiras colunas (sem mutar a original).
 * Posição/horário/turno/escala/unidade que só tinha plantão no dia extra sai da árvore
 * (não vira linha vazia nem conta em "Carregar mais"); `count` desconta esses plantões.
 */
export function trimListToDays(data: ListStationsResponse, days: number): ListStationsResponse {
  if (!data?.data) return data
  let removedCards = 0
  const trimPosition = (position: ListPosition): ListPosition | null => {
    const stations = position.stations.slice(0, days)
    const kept = cardCount(stations)
    removedCards += cardCount(position.stations) - kept
    return cardCount(position.stations) > 0 && kept === 0 ? null : { ...position, stations }
  }
  const trimHour = (hour: ListHour): ListHour | null => {
    const positions = pruneEmptied(hour.positions, hour.positions.map(trimPosition))
    return positions && { ...hour, positions }
  }
  const trimShift = (shift: ListShift): ListShift | null => {
    const hours = pruneEmptied(shift.hours, shift.hours.map(trimHour))
    return hours && { ...shift, hours }
  }
  const trimSchedule = (schedule: ListSchedule): ListSchedule | null => {
    const shifts = pruneEmptied(schedule.shifts, schedule.shifts.map(trimShift))
    return shifts && { ...schedule, shifts }
  }
  const trimUnit = (unit: ListUnit): ListUnit | null => {
    const schedules = pruneEmptied(unit.schedules, unit.schedules.map(trimSchedule))
    return schedules && { ...unit, schedules }
  }
  const trimCompany = (company: ListCompany): ListCompany | null => {
    const healthUnities = pruneEmptied(company.healthUnities, company.healthUnities.map(trimUnit))
    return healthUnities && { ...company, healthUnities }
  }
  const companies = data.data.map(trimCompany).filter((c): c is ListCompany => c !== null)
  return {
    ...data,
    count: typeof data.count === 'number' ? Math.max(0, data.count - removedCards) : data.count,
    tableHead: (data.tableHead ?? []).slice(0, days),
    data: companies
  }
}

const dayKey = (date: string) => date.slice(0, 10)

/** Mesma fórmula do BE (`calendar-summary.builder.ts`): 1 casa decimal; sem plantão = 100. */
const coveragePct = (filled: number, total: number) =>
  total === 0 ? 100 : Math.round((filled / total) * 1000) / 10

/**
 * Resumo do calendário só até `lastDay` (`yyyy-MM-dd`, inclusive). Os dias dentro do período
 * vêm do BE intactos (o BE já põe cada plantão no dia local da escala). O `summary` do BE
 * continua a fonte: dele só sai a contribuição do dia extra (cobertura recalculada com a
 * mesma fórmula só quando esse dia tinha plantão) e `criticalDays` é filtrado.
 */
export function trimCalendarSummary(
  summary: CalendarSummaryResponse,
  lastDay: string
): CalendarSummaryResponse {
  const inRange = (date: string) => dayKey(date) <= lastDay
  const days = (summary.days ?? []).filter((day) => inRange(day.date))
  const extraDays = (summary.days ?? []).filter((day) => !inRange(day.date))
  const schedules = (summary.schedules ?? [])
    .map((schedule) => ({ ...schedule, days: schedule.days.filter((day) => inRange(day.date)) }))
    // O BE só lista escala com plantão no período; uma que só tinha plantão no dia extra sai.
    .filter((schedule) => schedule.days.some((day) => day.total > 0))
  if (!summary.summary) return { ...summary, days, schedules }
  const critical = summary.summary.criticalDays
  const criticalDays = Array.isArray(critical) ? critical.filter(inRange) : critical
  const extraTotal = extraDays.reduce((acc, day) => acc + day.total, 0)
  if (extraTotal === 0) {
    return { ...summary, days, schedules, summary: { ...summary.summary, criticalDays } }
  }
  const extraOpen = extraDays.reduce((acc, day) => acc + day.open, 0)
  const total = summary.summary.total - extraTotal
  const open = summary.summary.open - extraOpen
  const filled = total - open
  return {
    ...summary,
    days,
    schedules,
    summary: {
      ...summary.summary,
      total,
      filled,
      open,
      coveragePct: coveragePct(filled, total),
      criticalDays
    }
  }
}
